import { useTranslation } from 'react-i18next';
import { Checkbox, Flex, Label } from '@kognitos/lattice';
import type { BooleanInputProps } from '../types';

/**
 * Boolean input component rendered as a checkbox
 *
 * Displays a checkbox with an optional label for boolean/toggle inputs.
 *
 * @param props - Component props
 * @returns JSX element containing the checkbox and label
 */
export function BooleanInput({
  name,
  checked,
  onChange,
  error,
  label,
  disabled,
}: BooleanInputProps) {
  const { t } = useTranslation('automation-inputs');

  return (
    <Flex
      align="center"
      gap={2}
      className={`px-3 py-2 h-9 rounded-md ${error ? 'border border-destructive' : ''}`}
    >
      <Checkbox
        id={`${name}-checkbox`}
        checked={checked}
        onCheckedChange={checked => onChange(!!checked)}
        aria-invalid={!!error}
        className={`bg-background data-[state=checked]:bg-primary ${error ? 'border-destructive' : ''}`}
        disabled={disabled}
        data-testid={`input-${name}`}
      />
      <Label
        htmlFor={`${name}-checkbox`}
        className="cursor-pointer font-normal"
      >
        {label || t('boolean.defaultLabel')}
      </Label>
    </Flex>
  );
}
